import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { RootState } from "../../../../../../redux/store";
import { ScanToContext } from "..";
import {
    ScanToMobilePreview,
    OffresPreviewData,
    PublicitesPreviewData,
    CommandePreviewData,
    DiversPreviewData,
} from "../View";

import { PropsType, ActionItem } from "./types";
import {
    DEFAULT_SECTION_TITLES,
    SECTION_ICONS,
    LANGUAGE_CODES,
    DEFAULT_ACTIONS,
} from "./constants";
import {
    CUSTOM_ICON_KEY,
    SCAN_TO_DEFAULT_MAIN_COLOR,
} from "../shared/constants";
import { useMultiCustomIcons } from "../shared/useMultiCustomIcons";
import {
    Container,
    LeftSection,
    RightSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
    PreviewToggleButton,
    ClosePreviewButton,
    OpenInNewWindowButton,
    PreviewOverlay,
} from "./styled";
import { SectionTitleCard } from "./SectionTitleCard";
import { SectionIconCard } from "../shared/SectionIconCard";
import { ActionsTable } from "./ActionsTable";

// ─── Component ───────────────────────────────────────────

export function Actions({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;

    const {
        sharedBannerData,
        sharedOffresData,
        sharedPublicitesData,
        sharedCommandeData,
        sharedDiversData,
        sharedPaiementData,
        sharedAppelServeurData,
        setSharedActionsData,
    } = React.useContext(ScanToContext);

    // ─── Languages (translated labels) ───────────────────
    const languages = React.useMemo(
        () =>
            LANGUAGE_CODES.map((l) => ({ code: l.code, label: t(l.labelKey) })),
        [t]
    );

    // ─── Section icons (translated labels) ───────────────
    const sectionIcons = React.useMemo(
        () =>
            SECTION_ICONS.map((i) => ({
                key: i.key,
                label: t(i.labelKey),
                path: i.path,
            })),
        [t]
    );

    // ─── Default section titles and actions from constants ─
    const defaultSectionTitles = DEFAULT_SECTION_TITLES;
    const defaultActions: ActionItem[] = DEFAULT_ACTIONS;

    // ─── State ───────────────────────────────────────────
    // Cast: ActionsConfig type doesn't declare every UI-state field used at
    // runtime (selectedLanguage, sectionTitles, etc.). Out of scope for ARCH-02.
    const savedActions = useSelector(
        (state: RootState) => state.scanTo?.actions
    ) as any;
    const [actionsEnabled, setActionsEnabled] = React.useState(
        savedActions?.actionsEnabled ?? true
    );
    const [sectionTitleVisible, setSectionTitleVisible] = React.useState(
        savedActions?.sectionTitleVisible ?? true
    );
    const [selectedLanguage, setSelectedLanguage] = React.useState(
        savedActions?.selectedLanguage || "fr"
    );
    const [selectedButtonsLang, setSelectedButtonsLang] = React.useState(
        savedActions?.selectedButtonsLang || "fr"
    );
    const [selectedIcon, setSelectedIcon] = React.useState(
        savedActions?.selectedIcon || "tag"
    );
    // Multi-icon state lifted into the shared hook so all 4 tabs
    // (Offres / Actions / MaCommande / Publicites) share one source of
    // truth — see `shared/useMultiCustomIcons.ts`.
    const {
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        setSelectedCustomIdx,
        customIconUrl,
        iconFileInputRef,
        handleIconUpload,
        removeCustomIcon,
    } = useMultiCustomIcons({
        saved: savedActions,
        fallbackIconKey: "tag",
        setSelectedIcon,
    });
    const [previewVisible, setPreviewVisible] = React.useState(true);

    const [sectionTitles, setSectionTitles] = React.useState<
        Record<string, string>
    >(savedActions?.sectionTitles || { ...defaultSectionTitles });

    // Initial actions: prefer the persisted list, otherwise seed with the
    // 4 default actions (Commander / Voir ma commande / Payer / Appeler
    // serveur). Falling back to [] used to render the section as fully
    // disabled by default — the user had to hit "Réinitialiser" just to
    // get the baseline buttons back.
    const [actions, setActions] = React.useState<ActionItem[]>(
        savedActions?.fullActions ?? defaultActions
    );

    const [iconPickerOpen, setIconPickerOpen] = React.useState<number | null>(
        null
    );
    const iconPickerRef = React.useRef<HTMLDivElement>(null);

    const [
        broadcastChannel,
        setBroadcastChannel,
    ] = React.useState<BroadcastChannel | null>(null);

    // bannerData and offresData come from context (sharedBannerData, sharedOffresData)

    // ─── Computed ────────────────────────────────────────
    const activeActions = React.useMemo(
        () =>
            [...actions]
                .filter((a) => a.active)
                .sort((a, b) => a.order - b.order),
        [actions]
    );

    // Auto-disable section if all actions are disabled
    const allActionsDisabled = React.useMemo(
        () => actions.every((a) => !a.active),
        [actions]
    );

    const effectiveEnabled = actionsEnabled && !allActionsDisabled;

    // ─── BroadcastChannel init (for external preview window only) ───
    React.useEffect(() => {
        const channel = new BroadcastChannel("scan-to-preview-channel");
        setBroadcastChannel(channel);
        return () => {
            channel.close();
        };
    }, []);

    // ─── Share actions data via context + BroadcastChannel ───
    React.useEffect(() => {
        const previewData = {
            source: "actions",
            actionsEnabled: effectiveEnabled,
            sectionTitleVisible,
            sectionTitle: sectionTitles[selectedLanguage],
            sectionIconPath:
                selectedIcon !== CUSTOM_ICON_KEY
                    ? sectionIcons.find((i) => i.key === selectedIcon)?.path ||
                      sectionIcons[0].path
                    : "",
            customIconUrl:
                selectedIcon === CUSTOM_ICON_KEY ? customIconUrl : "",
            actions: activeActions.map((a) => ({
                id: a.id,
                icon: a.icon,
                iconColor: a.iconColor,
                title: a.title[selectedButtonsLang] || "",
                description: a.description[selectedButtonsLang] || "",
                titleVisible: a.titleVisible,
                descriptionVisible: a.descriptionVisible,
            })),
            mainColor,
            selectedLanguage,
            selectedButtonsLang,
            // Full config for Redux persistence
            sectionTitles,
            selectedIcon,
            customIconUrls,
            customIconFiles: customIconUrls.map((url, i) => ({
                filename: customIconFilenames[i] || "",
                url,
            })),
            selectedCustomIdx,
            fullActions: actions,
        };

        // Share with other same-page tabs via context
        setSharedActionsData(previewData);

        // Send to external preview window via BroadcastChannel
        if (broadcastChannel) {
            try {
                broadcastChannel.postMessage(previewData);
            } catch (_) {
                /* channel closed */
            }
        }

        // Persist for external window initial load
        sessionStorage.setItem(
            "scanToActionsPreviewData",
            JSON.stringify(previewData)
        );
    }, [
        actionsEnabled,
        sectionTitleVisible,
        sectionTitles,
        selectedIcon,
        customIconUrl,
        customIconUrls,
        customIconFilenames,
        selectedCustomIdx,
        actions,
        selectedLanguage,
        selectedButtonsLang,
        mainColor,
        broadcastChannel,
        setSharedActionsData,
        activeActions,
        effectiveEnabled,
        sectionIcons,
    ]);

    // (Blob URL cleanup is now inside useMultiCustomIcons.)

    // ─── Close icon picker on outside click ──────────────
    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                iconPickerRef.current &&
                !iconPickerRef.current.contains(e.target as Node)
            ) {
                setIconPickerOpen(null);
            }
        };
        if (iconPickerOpen !== null) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [iconPickerOpen]);

    // ─── Handlers ────────────────────────────────────────

    const toggleActionActive = (actionId: number) => {
        setActions(
            actions.map((a) =>
                a.id === actionId ? { ...a, active: !a.active } : a
            )
        );
    };

    const toggleActionTitleVisible = (actionId: number) => {
        setActions(
            actions.map((a) =>
                a.id === actionId ? { ...a, titleVisible: !a.titleVisible } : a
            )
        );
    };

    const toggleActionDescVisible = (actionId: number) => {
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? { ...a, descriptionVisible: !a.descriptionVisible }
                    : a
            )
        );
    };

    const updateActionField = (
        actionId: number,
        field: "title" | "description",
        value: string
    ) => {
        setActions(
            actions.map((a) => {
                if (a.id !== actionId) return a;
                return {
                    ...a,
                    [field]: {
                        ...a[field],
                        [selectedButtonsLang]: value,
                    },
                };
            })
        );
    };

    const updateActionIcon = (actionId: number, iconKey: string) => {
        setActions(
            actions.map((a) =>
                a.id === actionId ? { ...a, icon: iconKey } : a
            )
        );
        setIconPickerOpen(null);
    };

    const updateActionColor = (actionId: number, color: string) => {
        setActions(
            actions.map((a) =>
                a.id === actionId ? { ...a, iconColor: color } : a
            )
        );
    };

    const resetSectionTitle = () => {
        setSectionTitles({
            ...sectionTitles,
            [selectedLanguage]: defaultSectionTitles[selectedLanguage],
        });
    };

    const resetAllSectionTitles = () => {
        setSectionTitles({ ...defaultSectionTitles });
        setSectionTitleVisible(true);
        setSelectedLanguage("fr");
    };

    const resetActionTitle = (actionId: number) => {
        const def = defaultActions.find((a) => a.id === actionId);
        if (!def) return;
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? {
                          ...a,
                          title: {
                              ...a.title,
                              [selectedButtonsLang]:
                                  def.title[selectedButtonsLang] || "",
                          },
                      }
                    : a
            )
        );
    };

    const resetActionDescription = (actionId: number) => {
        const def = defaultActions.find((a) => a.id === actionId);
        if (!def) return;
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? {
                          ...a,
                          description: {
                              ...a.description,
                              [selectedButtonsLang]:
                                  def.description[selectedButtonsLang] || "",
                          },
                      }
                    : a
            )
        );
    };

    const resetActionTexts = (actionId: number) => {
        const def = defaultActions.find((a) => a.id === actionId);
        if (!def) return;
        setActions(
            actions.map((a) =>
                a.id === actionId
                    ? {
                          ...a,
                          title: { ...def.title },
                          description: { ...def.description },
                          titleVisible: true,
                          descriptionVisible: true,
                      }
                    : a
            )
        );
    };

    const resetAllActions = () => {
        // Only reset action buttons (not section titles or icons)
        setActions(
            defaultActions.map((a) => ({
                ...a,
                title: { ...a.title },
                description: { ...a.description },
            }))
        );
        setSelectedButtonsLang("fr");
    };

    // (handleIconUpload + removeCustomIcon are provided by useMultiCustomIcons.)

    const openPreviewInNewWindow = () => {
        const previewData = {
            source: "actions",
            actionsEnabled: effectiveEnabled,
            sectionTitleVisible,
            sectionTitle: sectionTitles[selectedLanguage],
            sectionIconPath:
                selectedIcon !== CUSTOM_ICON_KEY
                    ? sectionIcons.find((i) => i.key === selectedIcon)?.path ||
                      sectionIcons[0].path
                    : "",
            customIconUrl:
                selectedIcon === CUSTOM_ICON_KEY ? customIconUrl : "",
            actions: activeActions.map((a) => ({
                id: a.id,
                icon: a.icon,
                iconColor: a.iconColor,
                title: a.title[selectedButtonsLang] || "",
                description: a.description[selectedButtonsLang] || "",
                titleVisible: a.titleVisible,
                descriptionVisible: a.descriptionVisible,
            })),
            mainColor,
            selectedLanguage,
        };
        sessionStorage.setItem(
            "scanToActionsPreviewData",
            JSON.stringify(previewData)
        );
        window.open(
            "/scan-to-preview",
            "ScanToPreview",
            "width=768,height=1024"
        );
    };

    // ─── JSX ─────────────────────────────────────────────
    return (
        <Container data-testid="scan-to-actions-container">
            {/* ─── Left: Configuration ─────────────────── */}
            <LeftSection>
                {/* Toggle activation */}
                <SectionHeader>
                    <SectionTitle>{t("Actions")}</SectionTitle>
                    <ToggleContainer>
                        <ToggleLabel>{t("Disable actions")}</ToggleLabel>
                        <Toggle
                            data-testid="scan-to-actions-enabled-toggle"
                            isActive={actionsEnabled}
                            mainColor={mainColor}
                            onClick={() => setActionsEnabled(!actionsEnabled)}
                            aria-label={t("Disable actions")}
                        />
                    </ToggleContainer>
                </SectionHeader>

                {allActionsDisabled && actionsEnabled && (
                    <div
                        style={{
                            padding: "10px 16px",
                            backgroundColor: "#fff3e0",
                            borderRadius: 8,
                            fontSize: 13,
                            color: "#e65100",
                            border: "1px solid #ffe0b2",
                        }}
                    >
                        {t(
                            "All actions are disabled. The section will be hidden."
                        )}
                    </div>
                )}

                {/* Section title (multilingual) */}
                <SectionTitleCard
                    mainColor={mainColor}
                    languages={languages}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                    sectionTitles={sectionTitles}
                    defaultSectionTitles={defaultSectionTitles}
                    onSectionTitleChange={(lang, value) =>
                        setSectionTitles({
                            ...sectionTitles,
                            [lang]: value,
                        })
                    }
                    sectionTitleVisible={sectionTitleVisible}
                    onSectionTitleVisibleToggle={() =>
                        setSectionTitleVisible(!sectionTitleVisible)
                    }
                    onResetSectionTitle={resetSectionTitle}
                    onResetAllSectionTitles={resetAllSectionTitles}
                />

                {/* Section icon */}
                <SectionIconCard
                    mode="multi"
                    testIdPrefix="scan-to-actions"
                    mainColor={mainColor}
                    sectionIcons={sectionIcons}
                    selectedIcon={selectedIcon}
                    onIconSelect={setSelectedIcon}
                    customIconUrls={customIconUrls}
                    selectedCustomIdx={selectedCustomIdx}
                    onSelectCustomIcon={(idx) => {
                        setSelectedCustomIdx(idx);
                        setSelectedIcon(CUSTOM_ICON_KEY);
                    }}
                    iconFileInputRef={iconFileInputRef}
                    onIconUpload={handleIconUpload}
                    onRemoveCustomIcon={removeCustomIcon}
                />

                {/* Action cards configuration */}
                <ActionsTable
                    mainColor={mainColor}
                    languages={languages}
                    selectedButtonsLang={selectedButtonsLang}
                    onButtonsLangChange={setSelectedButtonsLang}
                    actions={actions}
                    defaultActions={defaultActions}
                    iconPickerOpen={iconPickerOpen}
                    iconPickerRef={iconPickerRef}
                    onSetIconPickerOpen={setIconPickerOpen}
                    onToggleActionActive={toggleActionActive}
                    onToggleActionTitleVisible={toggleActionTitleVisible}
                    onToggleActionDescVisible={toggleActionDescVisible}
                    onUpdateActionField={updateActionField}
                    onUpdateActionIcon={updateActionIcon}
                    onUpdateActionColor={updateActionColor}
                    onResetActionTitle={resetActionTitle}
                    onResetActionDescription={resetActionDescription}
                    onResetActionTexts={resetActionTexts}
                    onResetAllActions={resetAllActions}
                />
            </LeftSection>

            {/* ─── Right: Preview ───────────────────────── */}
            <PreviewOverlay
                isVisible={previewVisible}
                onClick={() => setPreviewVisible(false)}
            />
            <PreviewToggleButton
                data-testid="scan-to-actions-preview-toggle"
                mainColor={mainColor}
                onClick={() => setPreviewVisible(true)}
            >
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                </svg>
            </PreviewToggleButton>

            <RightSection isVisible={previewVisible}>
                <ClosePreviewButton
                    data-testid="scan-to-actions-close-preview"
                    onClick={() => setPreviewVisible(false)}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </ClosePreviewButton>

                <OpenInNewWindowButton
                    data-testid="scan-to-actions-open-preview-window"
                    mainColor={mainColor}
                    onClick={openPreviewInNewWindow}
                    title={t("Open in new window")}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                </OpenInNewWindowButton>

                <ScanToMobilePreview
                    mainColor={mainColor}
                    selectedLanguage={selectedLanguage}
                    bannerData={sharedBannerData}
                    offresData={sharedOffresData as OffresPreviewData | null}
                    actionsData={{
                        actionsEnabled: effectiveEnabled,
                        sectionTitleVisible,
                        sectionTitle: sectionTitles[selectedLanguage],
                        sectionIconPath:
                            selectedIcon !== CUSTOM_ICON_KEY
                                ? sectionIcons.find(
                                      (i) => i.key === selectedIcon
                                  )?.path || sectionIcons[0].path
                                : "",
                        customIconUrl:
                            selectedIcon === CUSTOM_ICON_KEY
                                ? customIconUrl
                                : "",
                        actions: activeActions.map((a) => ({
                            id: a.id,
                            icon: a.icon,
                            iconColor: a.iconColor,
                            title: a.title[selectedButtonsLang] || "",
                            description:
                                a.description[selectedButtonsLang] || "",
                            titleVisible: a.titleVisible,
                            descriptionVisible: a.descriptionVisible,
                        })),
                    }}
                    publicitesData={
                        sharedPublicitesData as PublicitesPreviewData | null
                    }
                    commandeData={
                        sharedCommandeData as CommandePreviewData | null
                    }
                    diversData={sharedDiversData as DiversPreviewData | null}
                    paiementData={sharedPaiementData}
                    appelServeurData={sharedAppelServeurData}
                />
            </RightSection>
        </Container>
    );
}
